import logger from '../utils/logger';
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  Building2, Users, Wallet, TrendingUp, MapPin, Mail, Phone, 
  DollarSign, ArrowRight, Loader2, Globe, Shield, ChevronRight,
  Handshake, CreditCard, History, Settings, UserPlus, LogIn,
  Facebook, Instagram, Linkedin, Twitter, ExternalLink, Languages
} from 'lucide-react';
import { formatCurrency } from '../utils/currency';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AgencyDashboardPage() {
  const { slug } = useParams();
  const { language, toggleLanguage } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [agency, setAgency] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [error, setError] = useState(null);
  
  // Check if current user is agency admin
  const isAgencyAdmin = user && user.agency_id === agency?.agency_id && user.role === 'agency_admin';
  const isHubAdmin = user && user.role === 'admin';
  const canViewDashboard = isAgencyAdmin || isHubAdmin;
  
  // Translations
  const t = {
    fr: {
      loading: "Chargement...",
      notFound: "Agence non trouvée",
      notFoundDesc: "Cette agence n'existe pas ou n'est plus active.",
      backHome: "Retour à l'accueil",
      welcome: "Bienvenue chez",
      localCurrency: "Devise Locale",
      members: "Membres",
      transactions: "Transactions",
      volume: "Volume Total",
      contact: "Nous Contacter",
      joinUs: "Rejoindre cette agence",
      login: "Se connecter",
      register: "Créer un compte",
      alreadyMember: "Déjà membre?",
      newMember: "Nouveau?",
      adminDashboard: "Tableau de bord Admin",
      recentTransactions: "Transactions Récentes",
      allUsers: "Tous les Utilisateurs",
      wallets: "Portefeuilles",
      totalBalance: "Solde Total",
      noTransactions: "Aucune transaction",
      viewAll: "Voir tout",
      manageAgency: "Gérer l'agence",
      socialMedia: "Réseaux sociaux",
      discoverNetwork: "Découvrir le réseau",
      memberOfNetwork: "Membre du réseau des monnaies locales du Québec",
      memberOf: "Membre du réseau",
      joinCTA: "Créez votre compte et commencez à utiliser la monnaie locale!",
      topUp: "Recharge",
      transfer: "Transfert"
    },
    en: {
      loading: "Loading...",
      notFound: "Agency not found",
      notFoundDesc: "This agency doesn't exist or is no longer active.",
      backHome: "Back to home",
      welcome: "Welcome to",
      localCurrency: "Local Currency",
      members: "Members",
      transactions: "Transactions",
      volume: "Total Volume",
      contact: "Contact Us",
      joinUs: "Join this agency",
      login: "Log in",
      register: "Create account",
      alreadyMember: "Already a member?",
      newMember: "New here?",
      adminDashboard: "Admin Dashboard",
      recentTransactions: "Recent Transactions",
      allUsers: "All Users",
      wallets: "Wallets",
      totalBalance: "Total Balance",
      noTransactions: "No transactions",
      viewAll: "View all",
      manageAgency: "Manage agency",
      socialMedia: "Social Media",
      discoverNetwork: "Discover the network",
      memberOfNetwork: "Member of Quebec's local currency network",
      memberOf: "Member of",
      joinCTA: "Create your account and start using the local currency!",
      topUp: "Top-up",
      transfer: "Transfer"
    }
  };
  
  const text = t[language] || t.fr;
  
  // Fetch agency public profile
  useEffect(() => {
    async function fetchAgency() {
      try {
        const res = await fetch(`${API_URL}/api/agencies/public/${slug}`);
        if (!res.ok) {
          throw new Error('Agency not found');
        }
        const data = await res.json();
        setAgency(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    
    fetchAgency();
  }, [slug]);
  
  // Fetch dashboard data if admin
  useEffect(() => {
    async function fetchDashboard() {
      if (!agency || !user || !canViewDashboard) return;
      
      try {
        const res = await fetch(`${API_URL}/api/agencies/dashboard/${agency.agency_id}`, {
          credentials: 'include'
        });
        if (res.ok) {
          const data = await res.json();
          setDashboardData(data);
        }
      } catch (err) {
        logger.error('Failed to fetch dashboard:', err);
      }
    }
    
    fetchDashboard();
  }, [agency, user, canViewDashboard]);
  
  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-pink-500 mx-auto mb-4" />
          <p className="text-muted-foreground">{text.loading}</p>
        </div>
      </div>
    );
  }
  
  // Error state
  if (error || !agency) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-blue-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 text-center">
            <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <Building2 className="h-8 w-8 text-red-500" />
            </div>
            <h2 className="text-xl font-bold mb-2">{text.notFound}</h2>
            <p className="text-muted-foreground mb-6">{text.notFoundDesc}</p>
            <Button onClick={() => navigate('/')} variant="outline">
              {text.backHome}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  const primaryColor = agency.branding?.primary_color || '#EC4899';
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-blue-50">
      {/* Hero Section */}
      <div 
        className="relative overflow-hidden"
        style={{ 
          background: agency.branding?.background_image 
            ? `url(${agency.branding.background_image})` 
            : `linear-gradient(135deg, ${primaryColor}, ${primaryColor}dd)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className={`absolute inset-0 ${agency.branding?.background_image ? 'bg-black/50' : 'bg-black/10'}`} />
        <div className="relative max-w-6xl mx-auto px-4 py-12 sm:py-20">
          <div className="flex flex-col sm:flex-row items-center gap-6 text-white">
            <div 
              className="h-20 w-20 sm:h-28 sm:w-28 rounded-2xl flex items-center justify-center text-4xl sm:text-5xl font-bold bg-white/20 backdrop-blur-sm"
            >
              {agency.name?.charAt(0)?.toUpperCase() || 'A'}
            </div>
            <div className="text-center sm:text-left">
              {/* Welcome message with Language Toggle */}
              <div className="flex items-center justify-center sm:justify-start gap-3 mb-1">
                <p className="text-white/80 text-sm sm:text-base">{text.welcome}</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={toggleLanguage}
                  className="bg-white/20 hover:bg-white/30 border-white/30 text-white text-xs h-7 px-2"
                >
                  <Languages className="h-3.5 w-3.5 mr-1" />
                  {language === 'fr' ? 'EN' : 'FR'}
                </Button>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold">{agency.name}</h1>
              <div className="flex flex-wrap items-center justify-center sm:justify-start gap-3 mt-3 text-white/90">
                {agency.city && (
                  <span className="flex items-center gap-1 text-sm">
                    <MapPin className="h-4 w-4" />
                    {agency.city}{agency.region ? `, ${agency.region}` : ''}
                  </span>
                )}
                <span className="flex items-center gap-1 text-sm">
                  <DollarSign className="h-4 w-4" />
                  {agency.currency?.name} ({agency.currency?.symbol})
                </span>
              </div>
              
              {/* Social Links in Hero */}
              {agency.links && Object.values(agency.links).some(v => v) && (
                <div className="flex items-center justify-center sm:justify-start gap-3 mt-4">
                  {agency.links.website && (
                    <a href={agency.links.website} target="_blank" rel="noopener noreferrer" 
                       className="h-9 w-9 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors">
                      <Globe className="h-4 w-4" />
                    </a>
                  )}
                  {agency.links.facebook && (
                    <a href={agency.links.facebook} target="_blank" rel="noopener noreferrer"
                       className="h-9 w-9 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors">
                      <Facebook className="h-4 w-4" />
                    </a>
                  )}
                  {agency.links.instagram && (
                    <a href={agency.links.instagram} target="_blank" rel="noopener noreferrer"
                       className="h-9 w-9 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors">
                      <Instagram className="h-4 w-4" />
                    </a>
                  )}
                  {agency.links.linkedin && (
                    <a href={agency.links.linkedin} target="_blank" rel="noopener noreferrer"
                       className="h-9 w-9 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors">
                      <Linkedin className="h-4 w-4" />
                    </a>
                  )}
                  {agency.links.twitter && (
                    <a href={agency.links.twitter} target="_blank" rel="noopener noreferrer"
                       className="h-9 w-9 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors">
                      <Twitter className="h-4 w-4" />
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
          
          {agency.description && (
            <p className="mt-6 text-white/90 max-w-2xl text-center sm:text-left">
              {agency.description}
            </p>
          )}
        </div>
      </div>
      
      <div className="max-w-6xl mx-auto px-4 py-8 sm:py-12">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 -mt-8 sm:-mt-12 relative z-10 mb-8">
          <Card className="shadow-lg">
            <CardContent className="pt-6 text-center">
              <Users className="h-8 w-8 mx-auto mb-2" style={{ color: primaryColor }} />
              <p className="text-2xl sm:text-3xl font-bold">{agency.stats?.members || 0}</p>
              <p className="text-sm text-muted-foreground">{text.members}</p>
            </CardContent>
          </Card>
          
          <Card className="shadow-lg">
            <CardContent className="pt-6 text-center">
              <History className="h-8 w-8 mx-auto mb-2" style={{ color: primaryColor }} />
              <p className="text-2xl sm:text-3xl font-bold">{agency.stats?.transactions || 0}</p>
              <p className="text-sm text-muted-foreground">{text.transactions}</p>
            </CardContent>
          </Card>
          
          <Card className="shadow-lg">
            <CardContent className="pt-6 text-center">
              <TrendingUp className="h-8 w-8 mx-auto mb-2" style={{ color: primaryColor }} />
              <p className="text-2xl sm:text-3xl font-bold">
                {agency.currency?.symbol}{agency.stats?.volume?.toFixed(0) || 0}
              </p>
              <p className="text-sm text-muted-foreground">{text.volume}</p>
            </CardContent>
          </Card>
          
          <Card className="shadow-lg">
            <CardContent className="pt-6 text-center">
              <Globe className="h-8 w-8 mx-auto mb-2" style={{ color: primaryColor }} />
              <p className="text-xl sm:text-2xl font-bold">{agency.currency?.code}</p>
              <p className="text-sm text-muted-foreground">{text.localCurrency}</p>
            </CardContent>
          </Card>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Admin Dashboard (if authorized) */}
            {canViewDashboard && dashboardData && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5" style={{ color: primaryColor }} />
                    {text.adminDashboard}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                    <div className="p-4 bg-muted/50 rounded-xl text-center">
                      <p className="text-2xl font-bold">{dashboardData.stats?.total_users || 0}</p>
                      <p className="text-xs text-muted-foreground">{text.allUsers}</p>
                    </div>
                    <div className="p-4 bg-muted/50 rounded-xl text-center">
                      <p className="text-2xl font-bold">{dashboardData.stats?.total_wallets || 0}</p>
                      <p className="text-xs text-muted-foreground">{text.wallets}</p>
                    </div>
                    <div className="p-4 bg-muted/50 rounded-xl text-center">
                      <p className="text-2xl font-bold">
                        {agency.currency?.symbol}{dashboardData.stats?.total_balance?.toFixed(2) || '0.00'}
                      </p>
                      <p className="text-xs text-muted-foreground">{text.totalBalance}</p>
                    </div>
                    <div className="p-4 bg-muted/50 rounded-xl text-center">
                      <p className="text-2xl font-bold">{dashboardData.stats?.total_transactions || 0}</p>
                      <p className="text-xs text-muted-foreground">{text.transactions}</p>
                    </div>
                  </div>
                  
                  {/* Recent Transactions */}
                  <div>
                    <h4 className="font-medium mb-3">{text.recentTransactions}</h4>
                    {dashboardData.recent_transactions?.length > 0 ? (
                      <div className="space-y-2">
                        {dashboardData.recent_transactions.slice(0, 5).map((tx) => (
                          <div key={tx.transaction_id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className={`h-8 w-8 rounded-full flex items-center justify-center ${
                                tx.type === 'top_up' ? 'bg-green-100 text-green-600' : 'bg-blue-100 text-blue-600'
                              }`}>
                                {tx.type === 'top_up' ? <CreditCard className="h-4 w-4" /> : <ArrowRight className="h-4 w-4" />}
                              </div>
                              <div>
                                <p className="text-sm font-medium">
                                  {tx.type === 'top_up' ? text.topUp : tx.type === 'transfer' ? text.transfer : tx.type}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {new Date(tx.created_at).toLocaleString(language === 'fr' ? 'fr-CA' : 'en-CA')}
                                </p>
                              </div>
                            </div>
                            <p className={`font-medium ${tx.type === 'top_up' ? 'text-green-600' : ''}`}>
                              {tx.type === 'top_up' ? '+' : ''}{agency.currency?.symbol}{tx.amount?.toFixed(2)}
                            </p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-sm text-center py-4">{text.noTransactions}</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Join CTA (if not logged in or not member) */}
            {!user && (
              <Card className="border-2" style={{ borderColor: `${primaryColor}40` }}>
                <CardContent className="pt-6">
                  <div className="flex flex-col sm:flex-row items-center gap-6">
                    <div className="h-16 w-16 rounded-2xl flex items-center justify-center" style={{ backgroundColor: `${primaryColor}20` }}>
                      <Handshake className="h-8 w-8" style={{ color: primaryColor }} />
                    </div>
                    <div className="flex-1 text-center sm:text-left">
                      <h3 className="font-semibold text-lg mb-1">{text.joinUs}</h3>
                      <p className="text-muted-foreground text-sm">
                        {text.joinCTA}
                      </p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                      <Link to="/login">
                        <Button variant="outline" className="w-full sm:w-auto">
                          <LogIn className="h-4 w-4 mr-2" />
                          {text.login}
                        </Button>
                      </Link>
                      <Link to="/register">
                        <Button className="w-full sm:w-auto" style={{ backgroundColor: primaryColor }}>
                          <UserPlus className="h-4 w-4 mr-2" />
                          {text.register}
                        </Button>
                      </Link>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
          
          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contact Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{text.contact}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {agency.links?.website && (
                  <a 
                    href={agency.links.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors"
                  >
                    <Globe className="h-5 w-5" style={{ color: primaryColor }} />
                    <span className="text-sm truncate">{agency.links.website.replace(/^https?:\/\//, '')}</span>
                    <ExternalLink className="h-4 w-4 text-muted-foreground ml-auto" />
                  </a>
                )}
                {agency.contact?.email && (
                  <a 
                    href={`mailto:${agency.contact.email}`}
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors"
                  >
                    <Mail className="h-5 w-5" style={{ color: primaryColor }} />
                    <span className="text-sm">{agency.contact.email}</span>
                  </a>
                )}
                {agency.contact?.phone && (
                  <a 
                    href={`tel:${agency.contact.phone}`}
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors"
                  >
                    <Phone className="h-5 w-5" style={{ color: primaryColor }} />
                    <span className="text-sm">{agency.contact.phone}</span>
                  </a>
                )}
                
                {/* Social Links */}
                {agency.links && (agency.links.facebook || agency.links.instagram || agency.links.linkedin || agency.links.twitter) && (
                  <div className="pt-3 border-t">
                    <p className="text-xs text-muted-foreground mb-2 uppercase tracking-wide">
                      {text.socialMedia}
                    </p>
                    <div className="flex items-center gap-2">
                      {agency.links.facebook && (
                        <a href={agency.links.facebook} target="_blank" rel="noopener noreferrer"
                           className="h-9 w-9 rounded-lg bg-muted hover:bg-blue-100 flex items-center justify-center transition-colors">
                          <Facebook className="h-4 w-4 text-blue-600" />
                        </a>
                      )}
                      {agency.links.instagram && (
                        <a href={agency.links.instagram} target="_blank" rel="noopener noreferrer"
                           className="h-9 w-9 rounded-lg bg-muted hover:bg-pink-100 flex items-center justify-center transition-colors">
                          <Instagram className="h-4 w-4 text-pink-600" />
                        </a>
                      )}
                      {agency.links.linkedin && (
                        <a href={agency.links.linkedin} target="_blank" rel="noopener noreferrer"
                           className="h-9 w-9 rounded-lg bg-muted hover:bg-blue-100 flex items-center justify-center transition-colors">
                          <Linkedin className="h-4 w-4 text-blue-700" />
                        </a>
                      )}
                      {agency.links.twitter && (
                        <a href={agency.links.twitter} target="_blank" rel="noopener noreferrer"
                           className="h-9 w-9 rounded-lg bg-muted hover:bg-gray-200 flex items-center justify-center transition-colors">
                          <Twitter className="h-4 w-4 text-gray-700" />
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* PayHub Network */}
            <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
              <CardContent className="pt-6 text-center">
                <div className="h-12 w-12 mx-auto rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center text-white mb-3">
                  ⚜️
                </div>
                <h4 className="font-semibold text-blue-900">PayHub Québec+</h4>
                <p className="text-sm text-blue-700 mt-1">
                  {text.memberOfNetwork}
                </p>
                <Link to="/">
                  <Button variant="outline" size="sm" className="mt-4 border-blue-300 text-blue-700 hover:bg-blue-100">
                    {text.discoverNetwork}
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      
      {/* Footer */}
      <div className="border-t bg-white/50 py-6">
        <div className="max-w-6xl mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>⚜️ {agency.name} — {text.memberOf} PayHub Québec+</p>
        </div>
      </div>
    </div>
  );
}
